import { STATE_CATEGORY, StateHandlerRegistry } from './registry';

interface ConstructorParams {
  actionName: string;
  statePath: string;
  category?: string;
  initialState?: any;
  legacyOptions?: any;
}

/**
 * A base class for handling state changes in Redux.
 * Create actions to dispatch actions using the `update` method.
 *
 * Note: State handles are automatically registered on creation.
 */
export class ReduxStateHandler<T> {
  _actionName: string;

  _statePath: string[];

  _category: string;

  _initialState: any;

  _legacyOptions: any;

  constructor({
    actionName,
    statePath,
    category = STATE_CATEGORY.DATA,
    initialState = null,
    legacyOptions = {},
  }: ConstructorParams) {
    this._actionName = actionName;
    this._statePath = statePath.split('.');
    this._category = category;
    this._initialState = initialState;
    this._legacyOptions = legacyOptions;

    this.selector = this.selector.bind(this);
    this.update = this.update.bind(this);
    this.handleAction = this.handleAction.bind(this);

    StateHandlerRegistry.singleton.register(this);
  }

  get actionName() {
    return this._actionName;
  }

  get category() {
    return this._category;
  }

  /**
   * @example dispatch(userStateHandler.update({ name: 'John' }));
   * @param {*} value - The value to dispatch
   */
  update(value: any) {
    return {
      type: this._actionName,
      payload: value,
    };
  }

  selector(state: any) {
    const { legacyKey } = this._legacyOptions;

    const result = this._statePath.reduce(
      (acc, key) => acc?.[key],
      state[this.category],
    );

    if (legacyKey && result === undefined) {
      return state[this.category][legacyKey];
    }

    return result as T;
  }

  handleAction(state: any, action: any) {
    this._assignState(state, action.payload);
  }

  assignInitialState(state: any) {
    this._assignState(state, this._initialState);
  }

  _assignState(state: any, value: any) {
    const stateToUpdate = this._statePath.slice(0, -1).reduce((acc, key) => {
      acc[key] = acc[key] || {};
      return acc[key];
    }, state);
    const lastStateKey = this._statePath[this._statePath.length - 1]!;
    stateToUpdate[lastStateKey] = value;
  }
}
